import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, LogIn, LogOut, CheckCircle, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AuthCallback - Handles OAuth and URL-based SSO callbacks
 * 
 * Routes:
 * - /auth/callback?code=XXX&state=YYY (OAuth flow)
 * - /auth/sso?bs_user=XXX&bs_token=YYY&bs_time=ZZZ (URL-based SSO)
 */
export function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    handleAuth();
  }, []);

  const handleAuth = async () => {
    // Check for URL-based SSO params
    const bs_user = searchParams.get('bs_user');
    const bs_token = searchParams.get('bs_token');
    const bs_time = searchParams.get('bs_time');
    const redirect_to = searchParams.get('redirect_to') || '/';

    // Check for OAuth params
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const oauth_error = searchParams.get('error');

    if (oauth_error) {
      setStatus('error');
      setError(`Authentication failed: ${oauth_error}`);
      return;
    }

    try {
      let response;
      
      if (bs_user && bs_token && bs_time) {
        // URL-based SSO
        response = await fetch(
          `${BACKEND_URL}/api/auth/sso-login?bs_user=${encodeURIComponent(bs_user)}&bs_token=${bs_token}&bs_time=${bs_time}&redirect_to=${encodeURIComponent(redirect_to)}`
        );
      } else if (code && state) {
        // OAuth callback
        response = await fetch(
          `${BACKEND_URL}/api/auth/callback?code=${code}&state=${state}`
        );
      } else {
        setStatus('error');
        setError('No authentication parameters found');
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Authentication failed');
      }

      // Store user info in localStorage
      if (data.user) {
        // --- Clear any STALE local identity first (prevents cross-contamination,
        // e.g. a previous mock user like "Chad Robinson" lingering after SSO) ---
        const staleKeys = [
          'elchme_user_id', 'elchme_user_name', 'elchme_user_email',
          'workbook_user_id', 'workbook_user_name', 'workbook_user_email',
          'research_session_id', 'research_user',
          'elchme_access_token', 'elchme_id_token', 'elchme_sso_provider',
          'token'
        ];
        staleKeys.forEach((k) => localStorage.removeItem(k));

        // --- Write the real SSO identity under BOTH key conventions used across
        // the app (elchme_* for UserIndicator, workbook_* for Dashboard/workbook) ---
        const { id, name, email } = data.user;
        localStorage.setItem('elchme_user_id', id);
        localStorage.setItem('elchme_user_name', name || '');
        localStorage.setItem('elchme_user_email', email || '');
        localStorage.setItem('workbook_user_id', id);
        localStorage.setItem('workbook_user_name', name || '');
        localStorage.setItem('workbook_user_email', email || '');
        localStorage.setItem('elchme_sso_provider', 'belongingsessions');

        if (data.tokens) {
          localStorage.setItem('elchme_access_token', data.tokens.access_token);
          if (data.tokens.id_token) {
            localStorage.setItem('elchme_id_token', data.tokens.id_token);
          }
        }

        // Notify same-tab listeners (UserIndicator) that identity changed
        window.dispatchEvent(new Event('storage'));

        setUser(data.user);
      }

      setStatus('success');

      // Redirect after brief delay
      setTimeout(() => {
        navigate(data.redirect_to || redirect_to || '/');
      }, 1500);

    } catch (err) {
      console.error('Auth error:', err);
      setStatus('error');
      setError(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white/10 backdrop-blur-lg border-white/20">
        <CardHeader className="text-center">
          <CardTitle className="text-white flex items-center justify-center gap-2">
            {status === 'processing' && <Loader2 className="w-6 h-6 animate-spin text-amber-400" />}
            {status === 'success' && <CheckCircle className="w-6 h-6 text-green-400" />}
            {status === 'error' && <AlertCircle className="w-6 h-6 text-red-400" />}
            
            {status === 'processing' && 'Signing you in...'}
            {status === 'success' && 'Welcome!'}
            {status === 'error' && 'Sign In Failed'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === 'processing' && (
            <p className="text-center text-gray-300">
              Connecting with BelongingSessions.com...
            </p>
          )}

          {status === 'success' && user && (
            <div className="text-center space-y-2">
              <p className="text-white text-lg">Hello, {user.name}!</p>
              <p className="text-gray-400 text-sm">{user.email}</p>
              <p className="text-amber-400 text-sm">Redirecting...</p>
            </div>
          )}

          {status === 'error' && (
            <>
              <Alert variant="destructive" className="bg-red-900/50 border-red-500">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
              <div className="flex gap-2">
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                >
                  Go Home
                </Button>
                <Button
                  onClick={() => window.location.href = 'https://belongingsessions.com'}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-black"
                >
                  BelongingSessions
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * LogoutPage - Handles logout and session clearing
 */
export function LogoutPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');

  useEffect(() => {
    handleLogout();
  }, []);

  const handleLogout = async () => {
    try {
      // Clear all localStorage items
      localStorage.removeItem('elchme_user_id');
      localStorage.removeItem('elchme_user_name');
      localStorage.removeItem('elchme_user_email');
      localStorage.removeItem('elchme_access_token');
      localStorage.removeItem('elchme_id_token');
      localStorage.removeItem('elchme_sso_provider');
      localStorage.removeItem('workbook_user_id');
      localStorage.removeItem('workbook_user_name');
      localStorage.removeItem('workbook_user_email');
      localStorage.removeItem('research_session_id');
      localStorage.removeItem('research_user');
      localStorage.removeItem('token');

      // Notify backend (optional)
      try {
        await fetch(`${BACKEND_URL}/api/auth/logout`, { method: 'POST' });
      } catch (e) {
        // Ignore backend errors during logout
      }

      setStatus('success');

      // Redirect after brief delay
      setTimeout(() => {
        navigate('/');
      }, 1500);

    } catch (err) {
      console.error('Logout error:', err);
      setStatus('success'); // Still consider it success since localStorage is cleared
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white/10 backdrop-blur-lg border-white/20">
        <CardHeader className="text-center">
          <CardTitle className="text-white flex items-center justify-center gap-2">
            {status === 'processing' && <Loader2 className="w-6 h-6 animate-spin" />}
            {status === 'success' && <LogOut className="w-6 h-6 text-amber-400" />}
            
            {status === 'processing' ? 'Signing out...' : 'Signed Out'}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-gray-300">
            {status === 'processing' 
              ? 'Clearing your session...'
              : 'You have been signed out successfully.'}
          </p>
          
          {status === 'success' && (
            <div className="flex gap-2 justify-center">
              <Button
                onClick={() => navigate('/')}
                className="bg-amber-500 hover:bg-amber-600 text-black"
              >
                Go Home
              </Button>
              <Button
                onClick={() => window.location.href = 'https://belongingsessions.com'}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                BelongingSessions
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * SignInWithBelongingSessions - Button component for initiating SSO
 */
export function SignInWithBelongingSessions({ className = '', redirectTo = '/' }) {
  const [isLoading, setIsLoading] = useState(false);

  const handleSignIn = async () => {
    setIsLoading(true);
    
    try {
      // For now, redirect directly to belongingsessions.com login
      // In full OAuth implementation, this would call /api/auth/login first
      const siteUrl = window.location.origin;
      const loginUrl = `https://belongingsessions.com/login?redirect_to=${encodeURIComponent(siteUrl + '/auth/callback')}&app=elchme`;
      window.location.href = loginUrl;
    } catch (err) {
      console.error('Sign in error:', err);
      setIsLoading(false);
    }
  };

  return (
    <Button
      onClick={handleSignIn}
      disabled={isLoading}
      className={`bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black font-medium ${className}`}
    >
      {isLoading ? (
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
      ) : (
        <LogIn className="w-4 h-4 mr-2" />
      )}
      Sign in with BelongingSessions
    </Button>
  );
}

export default AuthCallback;
